import React, { FC, useMemo } from 'react';
import classnames from 'classnames';
import { createSignal } from '@funtech-int/rival';
import { useSignal } from '@funtech-int/rival-react';

import { Button } from '@/shared/components/Button/Button';
import { RouterLink } from '@/shared/components/RouterLink/RouterLink';
import { getTruncatedText } from '@/shared/lib/text';
import { personRoute } from '@/scenarios/person';
import { checkIsTvSeries } from '@/shared/lib/movie';

import { useMovieCard } from '../../hooks/useMovieCard';
import { I18n, useI18n } from '../../hooks/useI18n';

import styles from './MovieDetails.module.css';

const MAX_VISIBLE_ACTORS = 5;
const MAX_VISIBLE_DIRECTORS = 2;
const MAX_DESCRIPTION_LENGTH = 800;

type TrackItemData = {
    firstLine: string;
    secondLine: string;
};

interface NameListItem {
    name: string;
    id: string;
}

const NameListItem: FC<NameListItem> = ({ name, id }) => (
    <li className={styles.detailsListItem}>
        <RouterLink to={personRoute({ id })} className={styles.detailsListItemLink}>
            {name}
        </RouterLink>
    </li>
);

interface DetailsSectionProps {
    title: string;
    'data-test-id': string;
    children: React.ReactNode;
}

const DetailsSection: FC<DetailsSectionProps> = ({ title, 'data-test-id': testId, children }) => (
    <div data-test-id={testId} className={styles.detailsSection}>
        <p className={styles.detailsListTitle}>{title}</p>
        {children}
    </div>
);

interface TracksProps {
    title: string;
    testId: string;
    tracks: TrackItemData[];
    children?: React.ReactNode;
}

const Tracks: FC<TracksProps> = ({ testId, tracks, title, children }) => (
    <DetailsSection title={title} data-test-id={testId}>
        <ul>
            {tracks.map((track) => (
                <li
                    className={styles.detailsListItem}
                    key={`${track.firstLine}-${track.secondLine}`}
                >
                    <p>{track.firstLine}</p>
                    <p className={styles.detailsListItemSubtitle}>{track.secondLine}</p>
                </li>
            ))}
        </ul>
        {children}
    </DetailsSection>
);

interface MoreButtonInterface {
    onClick: () => void;
    count: number;
    i18n: I18n;
}
const MoreButton: FC<MoreButtonInterface> = ({ onClick, count, i18n }) => (
    <Button className={styles.more} size={32} theme="dark" onClick={onClick}>
        {i18n('content_details__action_more', {
            count,
        })}
    </Button>
);

interface MovieDetailsProps {
    contentId: string;
}

const SHOW_ALL_THRESHOLD = 4;
const VISIBLE_BEFORE_EXPAND = 3;

function useVisibleTracks<T>(tracks: T[]) {
    const controller = useMemo(() => {
        const showAllInitially = tracks.length <= SHOW_ALL_THRESHOLD;
        const visibleTracksSignal = createSignal<T[]>(
            showAllInitially ? tracks : tracks.slice(0, VISIBLE_BEFORE_EXPAND)
        );
        const showMoreButtonSignal = createSignal(
            !showAllInitially && tracks.length > SHOW_ALL_THRESHOLD
        );
        const hiddenCount =
            tracks.length > SHOW_ALL_THRESHOLD ? tracks.length - VISIBLE_BEFORE_EXPAND : 0;

        return {
            visibleTracksSignal: visibleTracksSignal.seal(),
            showMoreButtonSignal: showMoreButtonSignal.seal(),
            hiddenCount,
            expand: () => {
                visibleTracksSignal.set(tracks);
                showMoreButtonSignal.set(false);
            },
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const visibleTracks = useSignal(controller.visibleTracksSignal);
    const showMoreButton = useSignal(controller.showMoreButtonSignal);
    const hiddenCount = showMoreButton ? controller.hiddenCount : 0;

    return {
        visibleTracks,
        showMoreButton,
        hiddenCount,
        expand: controller.expand,
    };
}

export const MovieDetails: FC<MovieDetailsProps> = ({ contentId }) => {
    const { movie } = useMovieCard({ contentId });
    const i18n = useI18n();

    const { audioTracks, subtitleTracks } = useMemo(() => {
        if (!movie) {
            return {
                audioTracks: [],
                subtitleTracks: [],
            };
        }

        const { audioTracks } = movie;
        const { subtitleTracks } = movie;

        return {
            audioTracks: audioTracks.map((audioTrack) => ({
                firstLine: [
                    audioTrack.languageName,
                    audioTrack.studio,
                    audioTrack.forAdult ? i18n('content_details__for_adult') : undefined,
                ]
                    .filter(Boolean)
                    .join(' · '),
                secondLine: [audioTrack.qualityName, audioTrack.type].filter(Boolean).join(' · '),
            })),
            subtitleTracks: subtitleTracks.map((subtitleTrack) => ({
                firstLine: [
                    subtitleTrack.languageName,
                    subtitleTrack.forAdult ? i18n('content_details__for_adult') : undefined,
                ]
                    .filter(Boolean)
                    .join(' · '),
                secondLine: [subtitleTrack.type, subtitleTrack.studio].filter(Boolean).join(' · '),
            })),
        };
    }, [i18n, movie]);

    const audioTracksVisibility = useVisibleTracks(audioTracks);
    const subtitleTracksVisibility = useVisibleTracks(subtitleTracks);

    if (!movie) {
        return null;
    }

    const { features } = movie;
    const { actors, directors } = movie.filmingCrew;

    const description = movie.description
        ? getTruncatedText(movie.description, MAX_DESCRIPTION_LENGTH)
        : undefined;

    const isTvSeries = checkIsTvSeries(movie.typename);
    return (
        <div className={styles.root}>
            <div data-test-id="ContentDetails_description" className={styles.aboutBlock}>
                <p className={styles.title}>
                    {isTvSeries
                        ? i18n('content_details__series_text')
                        : i18n('content_details__movie_text')}
                </p>
                <p className={styles.descriptionText}>{description}</p>
            </div>

            <div className={styles.content}>
                <div className={styles.contentLeft} data-test-id="ContentDetails_left">
                    {actors.length > 0 && (
                        <DetailsSection
                            data-test-id="ContentDetails_actors"
                            title={i18n('content_details__actors_title')}
                        >
                            <ul>
                                {actors
                                    .slice(0, MAX_VISIBLE_ACTORS)
                                    .filter((actor) => actor.toString())
                                    .map((actor) => (
                                        <NameListItem
                                            key={actor.id}
                                            name={actor.toString() ?? ''}
                                            id={actor.id}
                                        />
                                    ))}
                            </ul>
                        </DetailsSection>
                    )}
                    {directors.filter((director) => director.toString()).length > 0 && (
                        <DetailsSection
                            data-test-id="ContentDetails_directors"
                            title={i18n('content_details__directors_title')}
                        >
                            <ul>
                                {directors
                                    .slice(0, MAX_VISIBLE_DIRECTORS)
                                    .filter((director) => director.toString())
                                    .map((director) => (
                                        <NameListItem
                                            key={director.id}
                                            name={director.toString() ?? ''}
                                            id={director.id}
                                        />
                                    ))}
                            </ul>
                        </DetailsSection>
                    )}
                    {features?.qualityFeatures && features?.qualityFeatures.length > 0 && (
                        <DetailsSection
                            data-test-id="ContentDetails_features"
                            title={i18n('content_details__features_title')}
                        >
                            <div className={styles.qualityFeatures}>
                                {features?.qualityFeatures.map((feature) => (
                                    <div
                                        key={feature.title}
                                        className={classnames(styles.qualityFeature, {
                                            [styles.qualityFeatureWithFilled]:
                                                feature.title === '4K',
                                        })}
                                        aria-label={feature.title}
                                    >
                                        {feature.title}
                                    </div>
                                ))}
                            </div>
                        </DetailsSection>
                    )}
                </div>
                <div className={styles.contentRight} data-test-id="ContentDetails_right">
                    {audioTracks.length > 0 && (
                        <Tracks
                            testId="ContentDetails_audioTracks"
                            title={i18n('content_details__audio_tracks_title')}
                            tracks={audioTracksVisibility.visibleTracks}
                        >
                            {audioTracksVisibility.showMoreButton && (
                                <MoreButton
                                    onClick={audioTracksVisibility.expand}
                                    count={audioTracksVisibility.hiddenCount}
                                    i18n={i18n}
                                />
                            )}
                        </Tracks>
                    )}
                    {subtitleTracks.length > 0 && (
                        <Tracks
                            testId="ContentDetails_subtitleTracks"
                            title={i18n('content_details__subtitle_tracks_title')}
                            tracks={subtitleTracksVisibility.visibleTracks}
                        >
                            {subtitleTracksVisibility.showMoreButton && (
                                <MoreButton
                                    onClick={subtitleTracksVisibility.expand}
                                    count={subtitleTracksVisibility.hiddenCount}
                                    i18n={i18n}
                                />
                            )}
                        </Tracks>
                    )}
                </div>
            </div>
        </div>
    );
};
